import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Gift, Calendar, DollarSign, CreditCard, User } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
const GiftVouchersTab = () => {
  const { token } = useSelector((state) => state.auth);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [usedData, setUsedData] = useState([]);
const { settings } = useSettings();
  // Fetch all vouchers
  useEffect(() => {
    const fetchVouchers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
         `${window.BASE_API_URL}myvouchers/${token}`
        );
        if (res.data.success) {
          setVouchers(res.data.data || []);
        } else {
          toast.error("Failed to load vouchers");
        }
      } catch (error) {
        console.error(error);
        toast.error("Error fetching vouchers");
      } finally {
        setLoading(false);
      }
    };
    fetchVouchers();
  }, [token]);

  // Fetch voucher usage history
  const handleOpenModal = async (voucher) => {
    setSelectedVoucher(voucher);
    try {
      const res = await axios.get(
        `${window.BASE_API_URL}usedvoucher/${voucher.id}`
      );
      if (res.data.success) {
        setUsedData(res.data.data || []);
      } else {
        setUsedData([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error fetching voucher usage details");
    }
  };

  const closeModal = () => {
    setSelectedVoucher(null);
    setUsedData([]);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-2xl font-semibold text-amber-700 mb-4"  style={{ color: settings?.cart_btn_color || "#111827" }}>
        My Gift Vouchers
      </h2>
      <p className="text-gray-600 mb-8">
        View and manage your purchased or received gift vouchers.
      </p>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading vouchers...</div>
      ) : vouchers.length === 0 ? (
        <div className="text-center text-gray-500 py-10">
          You have no gift vouchers yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {vouchers.map((v) => (
            <div
              key={v.id}
              onClick={() => handleOpenModal(v)}
              className="cursor-pointer group relative overflow-hidden rounded-2xl shadow-lg border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-amber-100 hover:shadow-xl transition-all duration-300"
            >
              <div className="p-5">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-amber-800 flex items-center gap-2">
                    <Gift className="w-5 h-5 text-amber-600" />
                    {v.voucher_name}
                  </h3>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      v.is_gift
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {v.is_gift ? "Gifted" : "Purchased"}
                  </span>
                </div>

                <div className="mt-4 space-y-1 text-gray-700 text-sm">
                  <p>
                    <b>Code:</b>{" "}
                    <span className="font-mono text-amber-700">{v.voucher_code}</span>
                  </p>
                  <p>
                    <b>Amount:</b> ${v.amount.toFixed(2)}
                  </p>
                  <p>
                    <b>Expiry:</b>{" "}
                    <span className="text-gray-600">{v.expiry_date}</span>
                  </p>
                </div>

                <div className="mt-5 flex justify-between text-xs text-gray-500">
                  <p>Payment: {v.payment_type}</p>
                  <p>{v.created_at?.split(" ")[0]}</p>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-amber-700 opacity-70" />
            </div>
          ))}
        </div>
      )}

      {/* Voucher Details Modal */}
      {selectedVoucher && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative">
            {/* Header */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>

            <h3 className="text-xl font-bold text-amber-700 mb-4 flex items-center gap-2">
              <Gift className="w-6 h-6 text-amber-600" /> Voucher Details
            </h3>

            {/* Voucher Info */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 mb-4">
              <p className="text-gray-800 font-semibold text-lg mb-1">
                {selectedVoucher.voucher_name}
              </p>
              <p className="text-sm text-gray-700">
                <b>Code:</b>{" "}
                <span className="font-mono text-amber-700">
                  {selectedVoucher.voucher_code}
                </span>
              </p>
              <p className="text-sm text-gray-700">
                <b>Amount:</b> ${selectedVoucher.amount}
              </p>
              <p className="text-sm text-gray-700">
                <b>Expiry Date:</b> {selectedVoucher.expiry_date}
              </p>
              <p className="text-sm text-gray-700">
                <b>Type:</b> {selectedVoucher.is_gift ? "Gift Voucher" : "Purchased"}
              </p>

              {selectedVoucher.is_gift === 1 && (
                <div className="mt-2 text-sm text-gray-700 border-t pt-2">
                  <b>Gifted To:</b> {selectedVoucher.gift_name} (
                  {selectedVoucher.gift_email})
                </div>
              )}
            </div>

            {/* Usage History */}
            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-2">
                💳 Usage History
              </h4>
              {usedData.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  This voucher hasn’t been used yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-amber-100 rounded-xl text-sm bg-white">
                    <thead className="bg-amber-50 text-amber-800">
                      <tr>
                        <th className="py-2 px-3 text-left">Order No</th>
                        <th className="py-2 px-3 text-left">Amount</th>
                        <th className="py-2 px-3 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usedData.map((u) => (
                        <tr
                          key={u.id}
                          className="border-t border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-2 px-3">{u.order_no}</td>
                          <td className="py-2 px-3 text-amber-700 font-semibold">
                            ${u.amount}
                          </td>
                          <td className="py-2 px-3 text-gray-600">
                            {u.created_at || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 text-right">
              <button
                onClick={closeModal}
                className="px-5 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftVouchersTab;
